/**
 * Android Back model (#1882, audit G8; decision D5): the topmost open overlay
 * closes first, then a non-default tab returns to Planning, then the app goes
 * to the background. Tabs are not history entries on native (App.jsx switches
 * them with replaceState), so Back never walks back through visited tabs.
 *
 *   overlays  a LIFO stack; components register through useBackHandler()
 *   root      one handler, owned by App: returns true when it consumed Back
 *             (e.g. it switched to Planning), false to let the app exit
 *
 * `handleBack()` is pure JS so it is unit-testable without Capacitor;
 * src/lib/nativeShell.js wires it to the Capacitor `backButton` event on
 * Android and, on iOS, src/lib/nativeEdgeSwipe.js calls it from the
 * edge-swipe-back gesture (only when `canGoBack()` says it would act).
 */
const overlays = [];
let rootHandler = null;
let rootCanGoBack = null;

/** Register an overlay's close handler. Returns its removal function. */
export function pushBackHandler(handler) {
  const entry = { handler };
  overlays.push(entry);
  return () => {
    const i = overlays.lastIndexOf(entry);
    if (i !== -1) overlays.splice(i, 1);
  };
}

/**
 * Install the tab-level handler (App). `canGo` is an optional predicate: true
 * when the handler would consume Back right now (a non-default tab is open).
 * Without it the root handler is assumed able to act. Returns a function that
 * clears both.
 */
export function setRootBackHandler(handler, canGo = null) {
  rootHandler = handler;
  rootCanGoBack = canGo;
  return () => {
    if (rootHandler === handler) {
      rootHandler = null;
      rootCanGoBack = null;
    }
  };
}

/**
 * Would Back do anything right now? True with an open overlay, or when the
 * root handler reports it would act. False means "exit": the iOS edge-swipe
 * gesture must not start (there is nothing to go back from).
 */
export function canGoBack() {
  if (overlays.length > 0) return true;
  if (!rootHandler) return false;
  if (!rootCanGoBack) return true;
  try { return rootCanGoBack() === true; } catch { return false; }
}

/**
 * Handle one Back press. Returns what consumed it:
 *   'overlay'  the topmost overlay closed
 *   'root'     the root handler acted (e.g. switched to Planning)
 *   'exit'     nothing left to close: the caller should background the app
 */
export function handleBack() {
  const top = overlays[overlays.length - 1];
  if (top) {
    try { top.handler(); } catch { /* a broken overlay must not trap Back */ }
    return 'overlay';
  }
  try {
    if (rootHandler && rootHandler() === true) return 'root';
  } catch { /* fall through to exit */ }
  return 'exit';
}

/** Number of open overlays (tests + diagnostics). */
export function openOverlayCount() {
  return overlays.length;
}

/** Test-only reset. */
export function __resetBackStackForTests() {
  overlays.length = 0;
  rootHandler = null;
  rootCanGoBack = null;
}
